import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

export enum ShipDeckHatchType {
  Raised = 'raised',
  RaisedWithScuttle = 'raised_with_scuttle',
  RaisedSpring = 'raised_spring',
  Flush = 'flush',
  Ramped = 'ramped',
}

export interface GeckoShipDeckHatchJSON {
  type: GeckoFeatureModelType.ShipDeckHatch
  name: string
  hatch_type: ShipDeckHatchType
  feature_geometry: {
    x: number
    y: number
    width: number
    height: number
  }
}

export const NewGeckoShipDeckHatchJSON: GeckoShipDeckHatchJSON = {
  type: GeckoFeatureModelType.ShipDeckHatch,
  name: 'New Ship Deck Hatch',
  hatch_type: ShipDeckHatchType.Flush,
  feature_geometry: {
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  },
}

export class GeckoShipDeckHatch extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckHatch
  readonly hatchType: ShipDeckHatchType
  readonly width: GeckoMeasurement
  readonly height: GeckoMeasurement
  readonly geometry: GeckoComponentCoords

  private constructor(
    id: string,
    name: string,
    hatchType: ShipDeckHatchType,
    width: GeckoMeasurement,
    height: GeckoMeasurement,
    geometry: GeckoComponentCoords
  ) {
    super(id)
    this.name = name
    this.hatchType = hatchType
    this.width = width
    this.height = height
    this.geometry = geometry
  }

  public static CreateFromJSON(
    componentID: string,
    hatchID: string,
    hatchJSON: GeckoShipDeckHatchJSON
  ): GeckoShipDeckHatch {
    const featureGeometryData = hatchJSON.feature_geometry as Record<
      string,
      number
    >
    const hatchGeometry = GeckoComponentCoords.CreateFromValues(
      componentID,
      featureGeometryData.x,
      GeckoMeasurementUnit.Meters,
      featureGeometryData.y,
      GeckoMeasurementUnit.Meters
    )

    const hatchName = hatchJSON.name as string

    const hatchType = hatchJSON.hatch_type as ShipDeckHatchType

    const width = GeckoMeasurement.CreateFromValue(
      featureGeometryData.width as number,
      GeckoMeasurementUnit.Meters
    )
    const height = GeckoMeasurement.CreateFromValue(
      featureGeometryData.height as number,
      GeckoMeasurementUnit.Meters
    )

    return new GeckoShipDeckHatch(
      hatchID,
      hatchName,
      hatchType,
      width,
      height,
      hatchGeometry
    )
  }

  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckHatchJSON>
  ): Map<string, GeckoShipDeckHatch> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckHatch.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckHatch>())
  }

  asJSON() {
    return generateHatch(
      this.name,
      this.hatchType,
      this.width,
      this.height,
      this.geometry.x,
      this.geometry.y
    )
  }
}

export function generateHatch(
  name: string,
  hatchType: ShipDeckHatchType,
  width: GeckoMeasurement,
  height: GeckoMeasurement,
  x: GeckoMeasurement,
  y: GeckoMeasurement
): GeckoShipDeckHatchJSON {
  return {
    type: GeckoFeatureModelType.ShipDeckHatch,
    name: name,
    hatch_type: hatchType,
    feature_geometry: {
      x: x.toNumber(GeckoMeasurementUnit.Meters),
      y: y.toNumber(GeckoMeasurementUnit.Meters),
      width: width.toNumber(GeckoMeasurementUnit.Meters),
      height: height.toNumber(GeckoMeasurementUnit.Meters),
    },
  }
}

export const ShipDeckHatchFormFields = (hatchJSON: GeckoShipDeckHatchJSON) => {
  return [
    {
      dataKey: 'name',
      label: 'Hatch Name',
      value: hatchJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'hatch_type',
      label: 'Hatch Type',
      value: hatchJSON.hatch_type,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      options: Object.keys(ShipDeckHatchType).map((key) => ({
        key: key,
        value: ShipDeckHatchType[key as keyof typeof ShipDeckHatchType],
        label: ShipDeckHatchType[key as keyof typeof ShipDeckHatchType]
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase()),
      })),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'width',
      label: 'Width',
      value: hatchJSON.feature_geometry.width,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'height',
      label: 'Height',
      value: hatchJSON.feature_geometry.height,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'x',
      label: 'X Position',
      value: hatchJSON.feature_geometry.x,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: hatchJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ShipDeckHatchJSONFromFormFields = (formFields: FormField[]) => {
  return generateHatch(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    getFormFieldByDataKey(formFields, 'hatch_type')?.value as ShipDeckHatchType,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'height')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'x')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const ShipDeckHatchFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckHatch,
  title: 'Hatches',
  singularName: 'Hatch',
  fields: ['hatch_type', 'width', 'height', 'x', 'y'],
}
